import {ReactNode} from 'react'
import {Button} from '@/components/ui/button/Button'
import {Text} from '@/components/ui/text/Text'
import {ArrowLeftIcon, CheckCircleIcon, CheckIcon, RefreshCcw05Icon, XCloseIcon} from '@/components/ui/icon/Icon'
import {useNotificationDescription} from '@/features/notifications/hooks/useNotificationDescription'
import {useNotificationWorkContext} from '@/features/notifications/hooks/useNotificationWorkContext'
import {useNotificationsPanelStore} from '@/features/notifications/store/useNotificationsPanelStore'
import {UserNotification} from '@/features/notifications/types'
import {useTranslation} from '@/translations/i18n'
import {
    NotificationContextBarVariant,
    StyledNotificationContextActions,
    StyledNotificationContextBack,
    StyledNotificationContextBar,
    StyledNotificationContextDescription,
    StyledNotificationContextTrailing,
    StyledVisuallyHidden
} from './style'

interface NotificationContextBarProps {
    variant: NotificationContextBarVariant
    /**
     * Rendered at the bar's trailing edge — used by a claiming host to keep its own close
     * control (FR-018b). Supplying it also suppresses the bar's own dismiss button: two close
     * affordances side by side read as a mistake, and the host's is the one that owns the
     * surface. The work context is still endable from there — "Torna alle notifiche" ends it,
     * and closing the host hands the task back to the wide bar, which keeps its own dismiss.
     */
    trailingSlot?: ReactNode
    /**
     * Closes the claiming host's own UI state so that "back to notifications" can get out of the
     * way of the notification list it is about to open.
     *
     * It must **not** touch the address: the bar performs the one navigation that clears both
     * the work context and the host's deep-link params. A second writer in the same tick would
     * restore what the first removed, because `setSearchParams` always builds from the calling
     * render's snapshot — and the host would then reopen itself from its own param.
     */
    onCloseHost?: () => void
}

/**
 * Composes the wide bar's single-line description from the parts the notification list shares
 * (FR-010): the task emphasised, followed by an attribution to the person who triggered it.
 *
 * A system-triggered notification has nobody to attribute, so it renders the title alone with
 * no dangling "da …" — deliberately not falling back to the list's "Sistema" sender label.
 *
 * The description stays put once the task is handled: the operator keeps seeing which task the
 * page belongs to while they carry on working. Only the icon changes — the notification's type
 * icon gives way to a check — which, with the green treatment, is what signals the new state
 * (FR-024). The explicit wording is not dropped, it moves to the live region below, so
 * assistive technology is still told in words what colour alone conveys visually.
 */
const NotificationContextDescription = ({
    notification,
    isConfirmed
}: {
    notification: UserNotification
    isConfirmed: boolean
}) => {
    const {t} = useTranslation()
    const {icon, title, conversationTitleLabel, conversationTitle, triggeredByPersonName} =
        useNotificationDescription(notification)

    // The server title names the conversation only for Conv-002 ("Nuovo messaggio in …"), never
    // for Conv-001, and this bar replaces the host surface's own header — so on a conversation
    // it composes the name itself rather than leave the operator without one.
    const description = conversationTitle ? `${conversationTitleLabel} "${conversationTitle}"` : title

    return (
        <StyledNotificationContextDescription $isConfirmed={isConfirmed} direction="row" align="center" gap={2}>
            {isConfirmed ? <CheckCircleIcon size={16} /> : icon}
            <Text render="span" size="textSm" palette="neutral" shade="900">
                <b>{description}</b>
                {triggeredByPersonName && ` ${t('notifications:context_attribution', {name: triggeredByPersonName})}`}
            </Text>
        </StyledNotificationContextDescription>
    )
}

/**
 * The work-context bar: names the task the operator is on and gives them the one explicit
 * action that ends it. Type-agnostic — every notification renders through the same path — and
 * form-agnostic: the destination surface picks `wide` or `hosted`, never the notification type.
 *
 * Renders nothing when there is no resolvable work context, in either variant.
 */
export const NotificationContextBar = ({variant, trailingSlot, onCloseHost}: NotificationContextBarProps) => {
    const {t} = useTranslation()
    const {notification, phase, isPending, markHandled, undo, dismiss, dismissWithHost} = useNotificationWorkContext()
    const openNotificationsPanel = useNotificationsPanelStore(state => state.open)

    const handleBackToNotifications = () => {
        // A hosting surface sits on top of everything, so it has to make way for the
        // notification list the operator is asking for.
        onCloseHost?.()
        // Going back to the queue ends the current task context: the operator is picking the
        // next one, so neither the bar nor the surface it was hosted in should follow them into
        // the notification list. The task itself is untouched — it stays pending until they say
        // otherwise. One navigation clears both params; see onCloseHost for why.
        dismissWithHost()
        openNotificationsPanel()
    }

    if (phase === 'idle' || !notification) return null

    const isConfirmed = phase === 'confirmed'

    return (
        <StyledNotificationContextBar
            $variant={variant}
            $isConfirmed={isConfirmed}
            role="region"
            aria-label={t('notifications:context_region_label', {description: notification.notification.title})}
        >
            <StyledNotificationContextBack>
                <Button variant="ghost" size="sm" onClick={handleBackToNotifications}>
                    <ArrowLeftIcon />
                    {t('notifications:context_back_to_notifications')}
                </Button>
            </StyledNotificationContextBack>

            {/* Only the wide bar describes the task. Hosted, it sits on top of a surface that
                already states what it is showing and why, in the room to do it properly — one
                ellipsised line inside a 500px panel said less than nothing. */}
            {variant === 'wide' && (
                <NotificationContextDescription notification={notification} isConfirmed={isConfirmed} />
            )}

            <StyledNotificationContextActions direction="row" align="center" gap={1}>
                {isConfirmed ? (
                    <Button variant="secondary" size="sm" onClick={undo} disabled={isPending}>
                        <RefreshCcw05Icon />
                        {t('notifications:reopen_task')}
                    </Button>
                ) : (
                    <Button variant="primary" size="sm" onClick={markHandled} disabled={isPending}>
                        <CheckIcon />
                        {t('notifications:context_mark_as_handled')}
                    </Button>
                )}
                {!trailingSlot && (
                    <Button
                        variant="ghost"
                        size="sm"
                        shape="square"
                        onClick={dismiss}
                        aria-label={t('notifications:context_dismiss')}
                        title={t('notifications:context_dismiss')}
                    >
                        <XCloseIcon />
                    </Button>
                )}
            </StyledNotificationContextActions>

            {trailingSlot && <StyledNotificationContextTrailing>{trailingSlot}</StyledNotificationContextTrailing>}

            <StyledVisuallyHidden aria-live="polite">
                {isConfirmed ? t('notifications:context_marked_as_handled') : ''}
            </StyledVisuallyHidden>
        </StyledNotificationContextBar>
    )
}
